import React, { useState } from "react";
import { context } from "../../../../../utils/Provider";
import { styled } from "@mui/material/styles";
import { Table, TableContainer } from "@mui/material";
import { useHeadroomContext } from "../../context/HeadRoomProvider";
import MDTypography from "components/MDTypography";
import TablePaginationWrapper, { MAX_ROWS } from "./TablePaginationWrapper";
import colors from "assets/theme-dark/base/colors";
import { useTableSort } from "../../hooks/useTableSort";
import TableToolbar from "./TableToolbar";
import DownloadIcon from "@mui/icons-material/Download";
import FunctionsIcon from "@mui/icons-material/Functions";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Column } from "../../HeadRoom";
import { useHeadroomFinalData } from "../../hooks/useHeadroomFinalResultsData";
import HeadroomFinalTableHeader from "./HeadroomFinalResultsTableHeader";
import HeadroomFinalTableBody from "./HeadroomFinalResultsTableBody";
import { useHeadroomTableSegments } from "../../hooks/useHeadroomTableSegments";
import { useHeadroomFinalSaveData } from "../../hooks/useSaveHeadroomFinalResults";
import { exportHeadroomFinalDataToExcel } from "../../utils/exportHeadroomFinalDataToExcel";

interface HeadroomFinalTableProps {
  columns?: Column[];
}

const HeadroomFinalTable: React.FC<HeadroomFinalTableProps> = ({ columns }) => {
  const { setIsLoading } = React.useContext(context);
  const {
    selectedProject,
    tableName,
    segmentGroups,
    driver1Type,
    driver1Mode,
    driver1NumeratorType,
    driver2Type,
    driver2Mode,
    driver2NumeratorType,
    headroomDriver,
  } = useHeadroomContext();

  const [increasedPenetration, setIncreasedPenetration] = useState<number>(0);

  const driverType = headroomDriver === "driver1" ? driver1Type : driver2Type;
  const driverMode = headroomDriver === "driver1" ? driver1Mode : driver2Mode;
  const driverNumeratorType = headroomDriver === "driver1" ? driver1NumeratorType : driver2NumeratorType;

  const {
    data,
    loading,
    error,
    totalCount,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    refetch,
    hasNextPage,
    hasPreviousPage,
    totals,
  } = useHeadroomFinalData(increasedPenetration);

  const { saveFinalData } = useHeadroomFinalSaveData();
  const { segments, hasOneDataAndOneManualSegment, isManualOnly } = useHeadroomTableSegments({ segmentGroups, data });
  const { sortColumn, sortDirection, handleSort } = useTableSort(segments, hasOneDataAndOneManualSegment);

  React.useEffect(() => {
    if (isManualOnly && rowsPerPage !== 100) {
      setRowsPerPage(MAX_ROWS);
      setPage(1);
    }
  }, [isManualOnly, rowsPerPage, setRowsPerPage, setPage]);

  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const maxPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    const boundedPage = Math.min(Math.max(1, newPage), maxPage);
    setPage(boundedPage);
    refetch({ offset: (boundedPage - 1) * rowsPerPage });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    refetch({ limit: newRowsPerPage, offset: 0 });
  };

  const handleCalculateHeadroomData = () => {
    refetch();
  };

  const handleExport = () => {
    exportHeadroomFinalDataToExcel({
      segments,
      driverType,
      driverMode,
      driverNumeratorType,
      tableName,
      data,
      sortColumn,
      sortDirection,
      increasedPenetration,
    });
  };

  const handleSaveData = async () => {
    if (data) {
      try {
        setIsLoading(true);
        await saveFinalData(increasedPenetration, data);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const toolbarButtons = [
    {
      icon: FunctionsIcon,
      tooltip: "Calculate Headroom Data",
      onClick: handleCalculateHeadroomData,
      disabled: !selectedProject,
    },
    {
      icon: SaveAltIcon,
      tooltip: "Save data",
      onClick: handleSaveData,
      disabled: !data,
    },
    {
      icon: DownloadIcon,
      tooltip: "Export to Excel",
      onClick: handleExport,
      disabled: !data,
    },
  ];

  if (loading) {
    return (
      <StyledTableContainer>
        <TableToolbar
          buttons={toolbarButtons}
          showDriverSelect={false}
          increasedPenetration={increasedPenetration}
          setIncreasedPenetration={setIncreasedPenetration}
        />
        <Table aria-label="skeleton headroom table">
          <HeadroomFinalTableHeader
            segments={segments}
            driverType={driverType}
            driverMode={driverMode}
            driverNumeratorType={driverNumeratorType}
            tableName={tableName}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
            isSkeleton={true}
          />
          <HeadroomFinalTableBody
            segments={segments}
            data={data}
            driverMode={driverMode}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
            isSkeleton={true}
          />
        </Table>
      </StyledTableContainer>
    );
  }

  if (error) {
    return (
      <StyledTableContainer>
        <TableToolbar
          buttons={toolbarButtons}
          showDriverSelect={false}
          increasedPenetration={increasedPenetration}
          setIncreasedPenetration={setIncreasedPenetration}
        />
        <MDTypography variant="body1" color="error.main" align="center" p={2}>
          Error: {error.message}
        </MDTypography>
      </StyledTableContainer>
    );
  }

  return (
    <StyledTableContainer>
      <TableToolbar
        buttons={toolbarButtons}
        showDriverSelect={false}
        increasedPenetration={increasedPenetration}
        setIncreasedPenetration={setIncreasedPenetration}
      />
      <Table aria-label="headroom final table">
        <HeadroomFinalTableHeader
          segments={segments}
          driverType={driverType}
          driverMode={driverMode}
          driverNumeratorType={driverNumeratorType}
          tableName={tableName}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
          hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
          isSkeleton={false}
          totals={totals}
        />
        <HeadroomFinalTableBody
          segments={segments}
          data={data}
          driverMode={driverMode}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
          isSkeleton={false}
        />
      </Table>
      {!isManualOnly && (
        <TablePaginationWrapper
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </StyledTableContainer>
  );
};

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(1),
  backgroundColor: colors.background.default,
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[4],
  padding: theme.spacing(1),
  border: `1px solid ${colors.grey[300]}`,
  overflowX: "auto",
  maxHeight: "800px",
  overflowY: "auto",
}));

export default HeadroomFinalTable;
